import React, {
  useState,
  useEffect,
  useRef,
  useId,
  forwardRef,
  useImperativeHandle,
  Ref
} from 'react';
import Image from 'next/legacy/image';

import { BaseInputProps, TextInputWidth } from '../shared';

import {
  MultiSelectWrapper,
  Dropdown,
  CheckboxesWrapper,
  CheckboxWrapper,
  CheckboxInput,
  CheckboxLabel,
  InputLabel
} from './style';
import { ErrorMessage, IconWrapper } from '../Select/style';

import icon from '@/images/error_icon.svg';

export interface MultiSelectOption {
  key: string;
  value: string;
}

interface MultiSelectProps extends Omit<BaseInputProps, 'value'> {
  options: MultiSelectOption[];
  handleChange: (value: string[]) => void;
  onFocus?: (isFocused: boolean) => void;
  value: string[];
  className?: string;
  single?: boolean;
  maxSelection?: number;
  search?: boolean;
  variant?: 'default' | 'preferences' | 'stateModal';
}

export const MultiSelect = forwardRef(
  (
    {
      options,
      value,
      type,
      label,
      error,
      handleChange,
      maxSelection,
      onFocus,
      width = TextInputWidth.FULL,
      className = '',
      single = false,
      variant = 'default',
      required
    }: MultiSelectProps,
    ref: Ref<unknown>
  ) => {
    const [isFirstTimeRender, setIsFirstTimeRender] = useState<boolean>(true);
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const [checkedValues, setCheckedValues] = useState<string[]>(value);

    const dropdownRef = useRef<HTMLDivElement>(null);
    const inputId = useId();

    useImperativeHandle(ref, () => ({
      clearValue() {
        setCheckedValues([]);
      },
      updateValues(val: string[]) {
        setCheckedValues(val);
      }
    }));

    useEffect(() => {
      if (isFirstTimeRender) {
        setIsFirstTimeRender(false);
      } else {
        handleChange(checkedValues);
      }
    }, [checkedValues]);

    useEffect(() => {
      function handleClickOutside(e: MouseEvent) {
        // parentElement is the component's root element, and because we want
        // to click the dropdown to close the options list when it is open,
        // we need to extend the validation to the root element, otherwise the
        // dropdown won't close
        if (
          dropdownRef.current &&
          !dropdownRef.current.parentElement?.contains(e.target as Node)
        )
          changeFocus(false);
      }

      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }, [dropdownRef]);

    const handleCheck = (key: string) => {
      const newCheckedValues = [...checkedValues];
      const alreadyCheckedIndex = checkedValues.findIndex((k) => k === key);

      if (alreadyCheckedIndex === -1) newCheckedValues.push(key);
      else newCheckedValues.splice(alreadyCheckedIndex, 1);

      if (maxSelection && newCheckedValues.length > maxSelection) {
        setCheckedValues(
          newCheckedValues.slice(newCheckedValues.length - maxSelection)
        );
      } else {
        setCheckedValues(newCheckedValues);
      }

      if (maxSelection === 1) {
        changeFocus(false);
      }
    };

    const formatValue = () => {
      let displayValue = '';

      if (single) {
        displayValue =
          value[0].length >= 10 ? value[0].slice(0, 10) + '...' : value[0];
      } else {
        displayValue =
          value.slice(0, -1).join(', ') +
          (value.length > 1 ? ' and ' : '') +
          value.slice(-1)[0];

        if (displayValue.length >= 10)
          displayValue = `${value.length} Selected`;
      }

      return displayValue;
    };

    const changeFocus = (focused: boolean) => {
      onFocus?.(focused);
      setIsFocused(focused);
    };

    const renderLabel = () => {
      const defaultLabel = variant === 'preferences' ? '' : label;
      return value.length > 0 ? formatValue() : defaultLabel;
    };

    const handleOptionKeyDown = (
      event: React.KeyboardEvent<HTMLInputElement>,
      optionIndex: number,
      optionValue: string,
      optionsCount: number
    ) => {
      // always close the dropdown when pressing Escape
      if (event.key === 'Escape') {
        cancelEvent(event);
        changeFocus(false);
        dropdownRef.current?.parentElement?.focus();
        return;
      }

      // in the case of Enter or Space, we just (un)check the option, without
      // closing the dropdown
      if (['Enter', 'Space'].includes(event.key)) {
        cancelEvent(event);
        handleCheck(optionValue);
        return;
      }

      // and for Tab, we only close the dropdown if navigating away from the
      // options list, and for that we check the first and last options
      if (event.key === 'Tab') {
        // if is the first option, there're more than 1 option and SHIFT was
        // pressed, then we're leaving the options list, so close it
        const isFirstOptionLeave =
          optionIndex === 0 && optionsCount > 1 && event.shiftKey;

        // if is the last option, and there's only 1 option or SHIFT was not
        // pressed, close it
        const isLastOptionLeave =
          optionIndex === optionsCount - 1 &&
          (!event.shiftKey || optionsCount === 1);

        if (isFirstOptionLeave || isLastOptionLeave) {
          cancelEvent(event);
          changeFocus(false);
          dropdownRef.current?.parentElement?.focus();
        }
      }
    };

    const handleWrapperKeyDown = (
      event: React.KeyboardEvent<HTMLElement>,
      focused: boolean
    ) => {
      // always close the dropdown when pressing Escape
      if (event.key === 'Escape') {
        cancelEvent(event);
        changeFocus(false);
      }
      // always open the dropdown when pressing Enter
      else if (event.key === 'Enter') {
        cancelEvent(event);
        changeFocus(focused);
      }
    };

    const cancelEvent = (event: React.KeyboardEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
    };

    return (
      <>
        <MultiSelectWrapper
          width={width}
          error={!!error}
          variant={variant}
          data-testid={`multiselect-${type}-${label}`}
          aria-label={label}
          title={label}
          tabIndex={0}
          onKeyDown={(evt) => handleWrapperKeyDown(evt, !isFocused)}
        >
          {variant === 'preferences' && (
            <InputLabel
              htmlFor={inputId}
              floatLabel={isFocused || !!value.length}
            >
              {label}
              {required ? '*' : ''}
            </InputLabel>
          )}

          <Dropdown
            id={`dropdown-${label}`}
            width={width}
            height={variant === 'preferences' ? 64 : undefined}
            error={!!error}
            onClick={() => changeFocus(!isFocused)}
            selected={!!value.length}
            className={className}
            variant={variant || 'default'}
            data-testid="multiselect-dropdown"
          >
            {renderLabel()}
          </Dropdown>

          <CheckboxesWrapper
            variant={variant}
            visible={isFocused}
            minified={options?.length <= 8}
            ref={dropdownRef}
            data-testid="multiselect-checkboxes-wrapper"
          >
            {!!options.length &&
              options.map(({ key, value: optValue }, index) => {
                return (
                  <CheckboxWrapper key={key}>
                    <CheckboxInput
                      type="checkbox"
                      name={label}
                      id={key}
                      value={optValue}
                      onClick={() => handleCheck(optValue)}
                      checked={!!checkedValues.find((k) => k === optValue)}
                      onChange={(e) => e.preventDefault()}
                      onKeyDown={(e) =>
                        handleOptionKeyDown(e, index, optValue, options.length)
                      }
                      data-testid={`checkbox-${key}`}
                    />
                    <CheckboxLabel htmlFor={key}>{optValue}</CheckboxLabel>
                  </CheckboxWrapper>
                );
              })}
          </CheckboxesWrapper>
        </MultiSelectWrapper>
        <ErrorMessage
          data-testid={`multiselect-${type}-${label}-error`}
          visible={!!error}
          variant={variant}
        >
          <IconWrapper>
            <Image src={icon} alt="Multiselect input error" />
          </IconWrapper>
          {error || ''}
        </ErrorMessage>
      </>
    );
  }
);
